import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Navigation, Bell, ShieldCheck, BookOpen, Building2 } from 'lucide-react';
import { directoryApi } from '../lib/api';
import { PageSEO, PageFAQSchema } from '../components/seo/SchemaMarkup';
import { HYROX_CITIES } from './hyrox/data';
import { RaceAlertCapture } from '../components/Articles/dfw/RaceAlertCapture';

const SITE_URL = 'https://ninjathlete.com';

const HUB_FAQS = [
  { question: 'Which cities have full interactive HYROX gym directories?', answer: 'We run interactive directories — with verified gyms, pricing, class times, race alerts and claim-your-listing for owners — in Dallas–Fort Worth, Houston, Austin, Denver, Tampa, Salt Lake City, Las Vegas, Portland and Anaheim, plus in-depth written guides for 13 more metros including New York, LA, Chicago and Miami.' },
  { question: 'How do you verify the gyms in these directories?', answer: 'Every listing is checked against official HYROX affiliate registries, the gym\'s own published programming, and — for featured partners — information submitted directly by the gym (schedules, equipment, coaching staff and photos).' },
  { question: 'I own a HYROX gym — how do I get listed or featured?', answer: 'Open your city\'s directory and use the "Claim this listing" button on your gym card, or submit your gym via the form on our HYROX Near Me page. Featured placements include photos, class schedules, coach profiles and a tracked membership link.' },
];

export default function HyroxGymsHubPage() {
  const [cities, setCities] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    directoryApi.getDirectoryIndex().then(({ data }) => setCities(data.cities || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const interactiveSlugs = new Set(cities.map((c) => c.slug));
    const all = [
      ...cities.map((c) => ({ name: `Best HYROX Gyms in ${c.label}`, slug: c.slug })),
      ...HYROX_CITIES.filter((c) => !interactiveSlugs.has(c.slug)).map((c) => ({ name: `Best HYROX Gyms in ${c.city}`, slug: c.slug })),
    ];
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'HYROX Gym Directories by City',
      numberOfItems: all.length,
      itemListElement: all.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, url: `${SITE_URL}/blogs/article/${c.slug}` })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'gyms-hub-citylist-jsonld';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, [cities]);

  const interactiveSlugs = new Set(cities.map((c) => c.slug));
  const guideOnly = HYROX_CITIES.filter((c) => !interactiveSlugs.has(c.slug));
  const totalGyms = cities.reduce((s, c) => s + c.gym_count, 0);

  return (
    <main className="pt-20 min-h-screen bg-[#050505]" data-testid="hyrox-gyms-hub-page">
      <PageSEO
        title="HYROX Gyms Near You | City-by-City Gym Directories | NinjAthlete"
        description="Every HYROX gym directory in one place — interactive city guides with verified gyms, pricing, class times, race-date alerts and claim-your-listing for gym owners."
        path="/hyrox/gyms"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'HYROX Hub', url: '/hyrox' }, { name: 'HYROX Gyms by City', url: '/hyrox/gyms' }]}
      />
      <PageFAQSchema faqs={HUB_FAQS} pageName="HYROX Gym Directories — FAQs" pagePath="/hyrox/gyms" scriptId="gyms-hub-faq-schema" />

      {/* Hero */}
      <section className="relative border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-6 py-16 sm:py-20">
          <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-4 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5" /> City Directories
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-5">
            HYROX Gyms <span className="text-[#e8ff00]">Near You</span> — Every City We Cover
          </h1>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl leading-relaxed mb-8">
            {totalGyms > 0 ? `${totalGyms} verified gyms across ` : ''}{cities.length || 9} interactive city directories — with pricing, class times, member videos and race-date alerts —
            plus in-depth written guides for {guideOnly.length} more US metros.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/hyrox/near-me" className="inline-flex items-center gap-2.5 px-7 py-4 bg-[#e8ff00] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity" data-testid="gyms-hub-near-me-btn">
              <Navigation className="w-4 h-4" /> Find Gyms by My Location
            </Link>
            <a href="#city-guides" className="inline-flex items-center gap-2 px-6 py-4 border border-zinc-700 text-zinc-300 font-heading text-sm uppercase tracking-wider hover:border-[#e8ff00]/50 hover:text-[#e8ff00] transition-colors">
              Browse All Cities <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Interactive directories */}
      <section className="max-w-5xl mx-auto px-6 py-14">
        <RaceAlertCapture
          cityId="dfw"
          cityLabel="Dallas–Fort Worth"
          variant="banner"
          eventNote="Sep 12 — Full HYROX Simulation at F45 Training Largo East (Tampa Bay). Get event invites + race-date alerts for your city:"
        />
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">Interactive Directories</p>
        <h2 className="font-heading text-lg text-white mb-2">Full City Directories — Verified Gyms, Pricing & Race Alerts</h2>
        <p className="text-zinc-500 text-sm mb-8 max-w-2xl">Live listings with official-club verification, class purchase links, member videos and city race-date email alerts.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gyms-hub-interactive-grid">
          {cities.map((c) => (
            <Link
              key={c.city}
              to={`/blogs/article/${c.slug}`}
              className="group bg-zinc-900/90 border border-zinc-800 hover:border-[#e8ff00]/40 p-5 transition-all flex flex-col"
              data-testid={`gyms-hub-city-${c.city}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#e8ff00]" />
                  <h3 className="text-white font-heading text-base leading-tight group-hover:text-[#e8ff00] transition-colors">{c.label}</h3>
                </div>
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading bg-zinc-800 text-zinc-400 border border-zinc-700 shrink-0">{c.state}</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500 mb-3">
                <span><strong className="text-[#e8ff00] font-heading">{c.gym_count}</strong> gyms</span>
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#e8ff00]" /> {c.official_count} official</span>
              </div>
              {c.featured_gym && (
                <p className="text-zinc-500 text-[11px] mb-3">Featured: <span className="text-zinc-300">{c.featured_gym}</span></p>
              )}
              <span className="mt-auto inline-flex items-center gap-1.5 text-[#e8ff00] text-xs font-mono uppercase tracking-wider group-hover:gap-2.5 transition-all">
                Open Directory <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
        <p className="mt-6 text-zinc-500 text-xs flex items-center gap-1.5">
          <Bell className="w-3.5 h-3.5 text-[#e8ff00]" /> Every directory includes free race-date email alerts for that city.
        </p>
      </section>

      {/* Written guides */}
      <section id="city-guides" className="max-w-5xl mx-auto px-6 py-14 border-t border-zinc-900">
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">More City Guides</p>
        <h2 className="font-heading text-lg text-white mb-2">In-Depth HYROX Gym Guides</h2>
        <p className="text-zinc-500 text-sm mb-8 max-w-2xl">Written directories with reviews, pricing and training tips for {guideOnly.length} more metros:</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="gyms-hub-guides-grid">
          {guideOnly.map((c) => (
            <Link key={c.slug} to={`/blogs/article/${c.slug}`} className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#e8ff00]/40 p-4 transition-all" data-testid={`gyms-hub-guide-${c.city.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-3.5 h-3.5 text-[#e8ff00]" />
                <span className="text-white text-sm font-heading group-hover:text-[#e8ff00] transition-colors">{c.city}</span>
              </div>
              <span className="text-zinc-600 text-xs">{c.state}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ + owner CTA */}
      <section className="max-w-5xl mx-auto px-6 py-14 border-t border-zinc-900">
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">FAQ</p>
        <h2 className="font-heading text-lg text-white mb-6">Common Questions</h2>
        <div className="space-y-3 mb-12">
          {HUB_FAQS.map((f) => (
            <details key={f.question} className="group bg-zinc-900/60 border border-zinc-800">
              <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none text-white text-sm font-heading">
                {f.question}
                <ArrowRight className="w-4 h-4 text-[#e8ff00] shrink-0 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="px-5 pb-5 text-zinc-400 text-sm leading-relaxed">{f.answer}</p>
            </details>
          ))}
        </div>
        <div className="bg-[#111] border border-[#e8ff00]/25 p-6" data-testid="gyms-hub-owner-cta">
          <p className="font-mono text-[10px] tracking-[0.2em] text-[#e8ff00] uppercase mb-2">Gym Owners</p>
          <h3 className="text-white font-heading text-base mb-2">Own a HYROX gym? Claim your listing — free.</h3>
          <p className="text-zinc-500 text-sm mb-4 max-w-2xl">Add photos, class times, pricing and a tracked membership link. Featured gyms also appear in our city race-alert emails.</p>
          <Link to="/hyrox/near-me" className="inline-flex items-center gap-2 px-5 py-3 bg-[#e8ff00] text-black text-xs font-heading uppercase tracking-widest hover:opacity-90 transition-opacity" data-testid="gyms-hub-submit-link">
            Submit Your Gym <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>
    </main>
  );
}
